import {
  CardHeader,
  CircularProgress,
  Typography,
  createStyles,
  makeStyles,
} from '@bb-ui/react-library';
import { useTenantContext } from 'contexts/TenantContext';
import useRestApi from 'hooks/useRestApi';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { apiUrl } from 'utils/apiUrl';
import { Edit } from '@bb-ui/icons/dist/small/Edit';
import { TenantInformationItem } from './TenantInformationItem';
import { TooltipIconButton } from '../../../components/TooltipIconButton';
import { useAuthorization } from '../../../hooks/useAuthorization';
import { TenantData } from '../../../App.types';
import { useSnackbar } from '../../../hooks/useSnackbar';
import { EditTenantDialog } from '../../../dialogs/EditTenantDialog';
import { SiteInformation } from 'components/SiteInformation/SiteInformation';
import { useLearnSiteContext } from 'contexts/LearnSiteContext/LearnSiteContext';
import { isLearnOrUnsetType } from 'utils/tenantType';
import { DataSharingSection } from './DataSharingSection';
import { LinkedIlluminateSection } from './LinkedIlluminateSection';

interface TenantReverseLookup {
  /**
   * Primary key of the lookup.
   */
  id: string;
  /**
   * ID of the tenant (probably redundant here).
   */
  tenantId: string;
  /**
   * Type of the lookup, e.g. `collab_lg`.
   */
  type: string;
  /**
   * Value of the lookup.
   */
  value: string;
}

interface TenantReverseLookupResponse {
  count: number;
  results: TenantReverseLookup[];
}

export const useStyles = makeStyles(() =>
  createStyles({
    heading: {
      marginBottom: '14px',
    },
    section: {
      marginBottom: '45px',
    },
  }),
);

/**
 * Returns the id of the tenant whose Illuminate links should be shown on this tenant's page.
 * Illuminate links are stored for Learn and Ally tenants (tenants without a type count as Learn),
 * so LearnInstance and LearnDms tenants use the Learn tenant above them.
 * Returns undefined when the tenant type cannot be linked, or the parent is not loaded yet.
 */
export const getIlluminateLinkTenantId = (
  tenant?: Partial<TenantData>,
  parentTenant?: Partial<TenantData>,
): string | undefined => {
  const isType = (t: Partial<TenantData> | undefined, type: string) =>
    t?.tenantType?.toLowerCase() === type.toLowerCase();

  if (!tenant) return undefined;
  if (isLearnOrUnsetType(tenant.tenantType) || isType(tenant, 'Ally')) return tenant.id;
  if (isType(tenant, 'LearnInstance')) return tenant.parentTenant;

  if (isType(tenant, 'LearnDms')) {
    // The parent can still be the one of the previous tenant while it is loading
    if (!parentTenant || parentTenant.id !== tenant.parentTenant) return undefined;
    if (isLearnOrUnsetType(parentTenant.tenantType)) return parentTenant.id;
    if (isType(parentTenant, 'LearnInstance')) return parentTenant.parentTenant;
  }

  return undefined;
};

export const TenantInformation: React.FC = (props) => {
  const { hasPermission } = useAuthorization();
  const classes = useStyles(props);
  const [dialogIsOpen, setDialogIsOpen] = React.useState(false);
  const { t } = useTranslation();
  const { site } = useLearnSiteContext();
  const {
    error: tenantError,
    loading: tenantLoading,
    tenant,
    parentTenant,
    fetch: tenantFetch,
  } = useTenantContext();
  const {
    doPatch,
    succeededRequests,
    failedRequests,
    clearSucceededRequests,
    clearFailedRequests,
  } = useRestApi(apiUrl('tenancy', 'tenants'), { manual: true });
  const {
    data: lookupsData,
    error: lookupsError,
    fetch,
    loading: lookupsLoading,
  } = useRestApi<TenantReverseLookupResponse>(
    apiUrl('tenancy', `tenants/reverseLookups?tenantId=${tenant?.id}`),
    {
      manual: true,
    },
  );

  const isLearnInstanceType = tenant?.tenantType === 'LearnInstance';

  const illuminateLinkTenantId = getIlluminateLinkTenantId(tenant, parentTenant);

  const { enqueueSnackbar } = useSnackbar();

  const canEditVisibility = hasPermission('tenant', 'update');

  function getParentTenantHref() {
    if (isLearnInstanceType || !tenant?.parentTenant) return undefined;

    const path = `/tenants/${tenant.parentTenant}`;
    return path;
  }

  function handleOnSave({
    parentTenant,
    description,
    hostname,
    region,
    tenantType,
  }: Partial<TenantData>) {
    doPatch(tenant?.id, { parentTenant, description, hostname, region, tenantType });
    setDialogIsOpen(false);
  }

  React.useEffect(() => {
    if (tenant?.id) {
      // lookupsError will reflect the error condition.
      fetch().catch(() => {});
    }
  }, [fetch, tenant]);

  React.useEffect(() => {
    if (succeededRequests.length > 0) {
      fetch!();
      tenantFetch!();
      clearSucceededRequests();
      enqueueSnackbar(t('tenantInformation.editSettingsSuccess'), { variant: 'success' });
    }
  }, [succeededRequests, clearSucceededRequests, fetch, tenantFetch, enqueueSnackbar, t]);

  React.useEffect(() => {
    if (failedRequests.length > 0) {
      clearFailedRequests();
      enqueueSnackbar(
        t('tenantInformation.editSettingsError', {
          message: failedRequests[0].error.message,
        }),
        { variant: 'error' },
      );
    }
  }, [failedRequests, clearFailedRequests, enqueueSnackbar, t]);

  let tenantInfo = (
    <CircularProgress
      ariaLabel={t('global.loadingIndicator.loading').toString()}
      data-testid="loading-tenant"
    />
  );

  if (tenantError) {
    tenantInfo = <Typography data-testid="tenant-error">{t('global.fetchError')}</Typography>;
  } else if (!tenantLoading) {
    tenantInfo = (
      <>
        <TenantInformationItem
          title={t('tenantInformation.tenantId')}
          value={isLearnInstanceType ? tenant?.parentTenant : tenant?.id}
        />
        <TenantInformationItem
          title={t('tenantInformation.parentTenantId')}
          value={isLearnInstanceType ? site?.ClientId : tenant?.parentTenant}
          href={getParentTenantHref()}
        />

        <TenantInformationItem
          title={t('tenantInformation.description')}
          value={tenant?.description}
        />
        <TenantInformationItem title={t('tenantInformation.hostname')} value={tenant?.hostname} />
        <TenantInformationItem title={t('tenantInformation.region')} value={tenant?.region} />
        <TenantInformationItem
          title={t('tenantInformation.tenantType')}
          value={tenant?.tenantType}
        />
      </>
    );
  }

  let lookupContent = (
    <CircularProgress
      ariaLabel={t('global.loadingIndicator.loading').toString()}
      data-testid="loading-lookups"
    />
  );

  if (lookupsError) {
    lookupContent = <Typography data-testid="lookups-error">{t('global.fetchError')}</Typography>;
  } else if (!lookupsLoading && lookupsData) {
    if (lookupsData.results.length === 0) {
      lookupContent = <Typography>{t('tenantInformation.noAddlIdentities')}</Typography>;
    } else {
      lookupContent = (
        <>
          {lookupsData.results.map((lookup: TenantReverseLookup) => (
            <TenantInformationItem title={lookup.type} value={lookup.value} key={lookup.type} />
          ))}
        </>
      );
    }
  }

  return (
    <div data-testid="tenant-information-page">
      {tenant?.tenantType !== 'LearnInstance' && tenant?.tenantType !== 'Learn' && (
        <div className={classes.section}>
          <CardHeader
            title={t('tenantInformation.pageTitle')}
            titleTypographyProps={{
              color: 'textSecondary',
              component: 'h3' as any,
              variant: 'h4',
            }}
            action={
              canEditVisibility && (
                <TooltipIconButton
                  aria-haspopup="dialog"
                  label={t('global.edit')}
                  onClick={() => setDialogIsOpen(true)}
                >
                  <Edit />
                </TooltipIconButton>
              )
            }
            className={classes.heading}
          />
          {tenantInfo}
        </div>
      )}
      <div>
        {(tenant?.tenantType === 'LearnInstance' || tenant?.tenantType === 'Learn') && (
          <SiteInformation />
        )}
      </div>
      <div className={classes.section}>
        <Typography className={classes.heading} component="h2" variant="h4">
          {t('tenantInformation.addlIdentities')}
        </Typography>
        {lookupContent}
      </div>
      {tenant?.tenantType === 'Illuminate' && <DataSharingSection tenant={tenant} />}
      {illuminateLinkTenantId && (
        <LinkedIlluminateSection
          tenantId={illuminateLinkTenantId}
          // A LearnDms tenant is the data export itself
          showDataExport={tenant?.tenantType !== 'LearnDms'}
        />
      )}
      <EditTenantDialog
        onClose={() => setDialogIsOpen(false)}
        onSave={handleOnSave}
        open={dialogIsOpen}
        tenant={tenant}
      />
    </div>
  );
};
